import React, { useState } from "react";
import { MapPin, Phone, Mail, Facebook, MessageCircle, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SCHOOL_INFO } from "@/lib/assets";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/public/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="page-contact">
      <section className="bg-[#1E3A8A] text-white py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="chip bg-white/10 border-white/20 text-[#FBBF24] mb-4"><span>Contact</span></div>
          <h1 className="mazid-headline text-5xl sm:text-6xl mb-4 max-w-3xl">
            Une question ? <span className="text-[#FBBF24]">Parlons-en.</span>
          </h1>
          <p className="text-lg text-slate-200 max-w-2xl">
            Notre équipe est disponible du lundi au samedi pour répondre à toutes vos questions.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-slate-200" data-testid="contact-info-address">
              <MapPin className="h-7 w-7 text-[#166534] mb-3" />
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Adresse</div>
              <div className="text-[#1E3A8A] font-semibold leading-snug">{SCHOOL_INFO.address}</div>
            </div>

            <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-slate-200" data-testid="contact-info-phone">
              <Phone className="h-7 w-7 text-[#EA580C] mb-3" />
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Téléphone</div>
              <div className="space-y-1">
                {SCHOOL_INFO.phones.map((p, i) => (
                  <div key={i} className="text-[#1E3A8A] font-semibold">{p}</div>
                ))}
              </div>
            </div>

            <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-slate-200" data-testid="contact-info-email">
              <Mail className="h-7 w-7 text-[#1E3A8A] mb-3" />
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Email</div>
              <div className="text-[#1E3A8A] font-semibold">{SCHOOL_INFO.email}</div>
            </div>

            <div className="flex gap-3">
              <a href={SCHOOL_INFO.facebook} target="_blank" rel="noreferrer" className="flex-1 bg-[#1E3A8A] text-white p-5 rounded-2xl flex items-center gap-3 hover:bg-[#172554] transition" data-testid="contact-fb-link">
                <Facebook className="h-5 w-5" />
                <span className="font-semibold text-sm">Facebook</span>
              </a>
              <a href={`https://wa.me/${SCHOOL_INFO.whatsapp.replace(/\s|\+/g, "")}`} target="_blank" rel="noreferrer" className="flex-1 bg-[#166534] text-white p-5 rounded-2xl flex items-center gap-3 hover:bg-[#14532d] transition" data-testid="contact-wa-link">
                <MessageCircle className="h-5 w-5" />
                <span className="font-semibold text-sm">WhatsApp</span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-7 lg:p-10 space-y-5" data-testid="contact-form">
              <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] mb-2">Envoyez-nous un message</h2>
              <p className="text-sm text-slate-600 mb-6">Réponse garantie sous 24h ouvrables.</p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nom complet *</Label>
                  <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
              </div>
              <div>
                <Label>Sujet *</Label>
                <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} required />
              </div>
              <div>
                <Label>Message *</Label>
                <Textarea data-testid="contact-message" rows={6} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required />
              </div>
              <Button type="submit" disabled={loading} className="bg-[#EA580C] hover:bg-orange-700 text-white w-full sm:w-auto gap-2" data-testid="contact-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Envoyer le message</>}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
